import { toPng } from 'html-to-image';
import Pptxgen from 'pptxgenjs';
import foresightLogoFooter from 'styles/assets/png/foresight_logo_footer.png';
import { ImageExists } from 'utilitys/helpers/general';

import { Widget } from 'components/Dashboards/Reporting/reporting.types';
import {
    CommonExportConstants,
    getUniqueProjectContracts,
} from 'components/pages/ReportPage/hooks/commonExportUtils/commonExportUtils';
import { WidgetPosition } from 'components/pages/ReportPage/hooks/useExportReportToPdf/useExportReportToPdf.utils';

export class PptxExportUtils {
    // PowerPoint slide dimensions (standard 10" x 7.5")
    static readonly PPTX_SLIDE_WIDTH = 10; // inches
    static readonly PPTX_SLIDE_HEIGHT = 7.5; // inches

    // Layout constants for PowerPoint
    static readonly PPTX_MARGIN = 0.2; // inches (left/right margins) - minimal for maximum width
    static readonly PPTX_HEADER_HEIGHT = 1.3; // inches (for title at 2% and subtitle at 6% + padding)
    static readonly PPTX_FOOTER_HEIGHT = 1.0; // inches (for footer at 95% with orange line + text + padding)
    static readonly PPTX_CONTENT_WIDTH = PptxExportUtils.PPTX_SLIDE_WIDTH - PptxExportUtils.PPTX_MARGIN * 2; // 9.0 inches
    static readonly PPTX_CONTENT_HEIGHT =
        PptxExportUtils.PPTX_SLIDE_HEIGHT - PptxExportUtils.PPTX_HEADER_HEIGHT - PptxExportUtils.PPTX_FOOTER_HEIGHT; // 5.2 inches
    static readonly PPTX_HORIZONTAL_GAP = 0.2; // inches between widgets
    static readonly PPTX_START_Y = PptxExportUtils.PPTX_HEADER_HEIGHT - 1; // Start after header with offset (0.3")

    // Logo positions
    static readonly POWERED_Y_POSITION = '93.4%';
    static readonly FOOTER_LOGO_Y_POSITION = '94.4%';

    // Safe zone boundaries - stricter to prevent overflow with devtools open
    static readonly SAFE_TOP_BOUNDARY = 1.3; // Stay below header content
    static readonly SAFE_BOTTOM_BOUNDARY = 5.5; // Stay above footer content with more margin

    /**
     * Creates a component lookup map by ID for O(1) access
     */
    static createWidgetByIdMap(reportComponents: Widget[]): Map<string, Widget> {
        const componentById = new Map<string, Widget>();
        reportComponents.forEach((comp) => {
            componentById.set(comp.id.toString(), comp);
        });
        return componentById;
    }

    /**
     * Maps widget elements to their component data using widgetId
     */
    static createElementToRealWidgetMap(
        widgets: WidgetPosition[],
        componentById: Map<string, Widget>,
    ): Map<HTMLElement, Widget> {
        const elementToRealWidgetMapMap = new Map<HTMLElement, Widget>();
        widgets.forEach((widget) => {
            if (widget.widgetId) {
                const component = componentById.get(widget.widgetId);
                if (component) {
                    elementToRealWidgetMapMap.set(widget.element, component);
                }
            }
        });
        return elementToRealWidgetMapMap;
    }

    /**
     * Gets the logo data URL from user's company logo
     */
    static async getLogoDataUrl(logo?: string): Promise<string | undefined> {
        if (!logo) return undefined;

        const isLogoExists = await ImageExists(logo);
        if (!isLogoExists) return undefined;

        return new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                if (ctx) {
                    ctx.drawImage(img, 0, 0);
                    resolve(canvas.toDataURL('image/png'));
                } else {
                    resolve(undefined);
                }
            };
            img.onerror = () => resolve(undefined);
            img.src = logo;
        });
    }

    /**
     * Defines the master slide with header/footer elements
     */
    static defineMasterSlide(pptx: Pptxgen, logoDataUrl: string | undefined): void {
        const masterSlideObjects: any[] = [
            // Top orange line
            {
                line: {
                    x: '3%',
                    y: '3%',
                    h: 0,
                    w: '93%',
                    line: { color: '000000', width: 1 },
                },
            },
            // Bottom black line
            {
                line: {
                    x: '3%',
                    y: '92%',
                    h: 0,
                    w: '93%',
                    line: { color: '000000', width: 1 },
                },
            },
            // "Powered by" text
            {
                text: {
                    text: 'Powered by',
                    options: {
                        x: '2%',
                        y: PptxExportUtils.POWERED_Y_POSITION,
                        fontSize: 6,
                        color: '#000000',
                    },
                },
            },
            // Foresight footer logo
            {
                image: {
                    x: '3%',
                    y: PptxExportUtils.FOOTER_LOGO_Y_POSITION,
                    path: foresightLogoFooter,
                    w: '14%',
                    h: '4%',
                },
            },
        ];

        // Add user's company logo at top right (from useUserHook)
        if (logoDataUrl) {
            masterSlideObjects.push({
                image: {
                    data: logoDataUrl,
                    x: '91%',
                    y: '4%',
                    sizing: { type: 'contain', w: '6%', h: '6%' },
                },
            });
        }

        pptx.defineSlideMaster({
            title: 'MASTER_SLIDE',
            background: { color: 'FFFFFF' },
            objects: masterSlideObjects,
            slideNumber: {
                x: '93%',
                y: '93.8%',
                color: '#000000',
                fontSize: 8,
            },
        });
    }

    /**
     * Calculates row scaling based on widget dimensions
     * This ensures maximum use of space while preventing overflow
     */
    static calculateRowScaling(row: WidgetPosition[]): {
        scale: number;
        scaledRowWidth: number;
        scaledRowHeight: number;
        scaledGap: number;
    } {
        // Calculate total row dimensions in pixels (screen zoom independent)
        const rowWidthInches = row.reduce((sum, widget) => sum + widget.width / 96, 0);
        const rowHeightInches = Math.max(...row.map((widget) => widget.height / 96));

        // Calculate gap width (n-1 gaps for n widgets)
        const totalGapWidth = (row.length - 1) * PptxExportUtils.PPTX_HORIZONTAL_GAP;
        const totalRowWidthWithGaps = rowWidthInches + totalGapWidth;

        // Calculate scale factors to fit within content area
        // Use slightly less than full width/height to allow for maximum scaling
        const widthScale =
            totalRowWidthWithGaps > PptxExportUtils.PPTX_CONTENT_WIDTH
                ? (PptxExportUtils.PPTX_CONTENT_WIDTH * 0.98) / totalRowWidthWithGaps // 2% margin
                : 1;
        const heightScale =
            rowHeightInches > PptxExportUtils.PPTX_CONTENT_HEIGHT
                ? (PptxExportUtils.PPTX_CONTENT_HEIGHT * 0.98) / rowHeightInches // 2% margin
                : 1;

        // Use the smaller scale to ensure everything fits
        let scale = Math.min(widthScale, heightScale);

        // If content is much smaller than available space, scale it up (but not too much)
        const maxScaleUp = 1.4; // Allow up to 40% larger
        if (scale > maxScaleUp) {
            scale = maxScaleUp;
        }

        // Check if content would overflow into header/footer with current scale
        const testScaledHeight = rowHeightInches * scale;
        const testRowStartY =
            PptxExportUtils.PPTX_START_Y + (PptxExportUtils.PPTX_CONTENT_HEIGHT - testScaledHeight) / 2;
        const testRowEndY = testRowStartY + testScaledHeight;

        // Only reduce if content would actually overflow the safe zones
        const wouldOverflowTop = testRowStartY < PptxExportUtils.SAFE_TOP_BOUNDARY;
        const wouldOverflowBottom = testRowEndY > PptxExportUtils.SAFE_BOTTOM_BOUNDARY;

        if (wouldOverflowTop || wouldOverflowBottom) {
            // Calculate exact scale needed to fit within safe boundaries
            const availableHeight = PptxExportUtils.SAFE_BOTTOM_BOUNDARY - PptxExportUtils.SAFE_TOP_BOUNDARY;
            const requiredScale = availableHeight / rowHeightInches;
            scale = Math.min(scale, requiredScale * 0.95); // Use 95% of required to be safe
        }

        // Calculate final dimensions (everything scaled together, including gaps)
        const scaledRowWidth = totalRowWidthWithGaps * scale;
        const scaledRowHeight = rowHeightInches * scale;
        const scaledGap = PptxExportUtils.PPTX_HORIZONTAL_GAP * scale;

        return { scale, scaledRowWidth, scaledRowHeight, scaledGap };
    }

    /**
     * Adds footer text with hyperlink to a slide
     */
    static addFooterLink(slide: any, url: string): void {
        slide.addText(
            [
                {
                    text: 'Dive into more details in ',
                    options: {
                        color: '666666',
                        fontSize: 8,
                    },
                },
                {
                    text: 'Foresight platform',
                    options: {
                        color: 'FF6600',
                        fontSize: 8,
                        hyperlink: { url },
                    },
                },
                {
                    text: ' (login required)',
                    options: {
                        color: '666666',
                        fontSize: 8,
                    },
                },
            ],
            {
                x: '7%',
                y: '95.9%',
                w: '80%',
                align: 'center',
            },
        );
    }

    /**
     * Converts a data URL to a Blob
     */
    static dataURLToBlob(dataURL: string): Blob {
        const parts = dataURL.split(',');
        const byteString = atob(parts[1]);
        const mimeString = parts[0].split(':')[1].split(';')[0];
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }
        return new Blob([ab], { type: mimeString });
    }

    /**
     * Captures a widget as an image with optimized settings for PPTX export
     * Uses html-to-image library for capture
     */
    static async captureWidgetAsImage(widget: WidgetPosition, widgetCount: number): Promise<string> {
        try {
            // Reduce pixel ratio for large reports to prevent memory issues
            const pixelRatio = widgetCount > 20 ? 2 : 3;

            // Use captureElement directly - this is the actual widget content (WidgetInReport)
            // already resolved in getWidgetPositionsFromDOM
            const dataUrl = await toPng(widget.captureElement, {
                pixelRatio: pixelRatio,
                backgroundColor: '#ffffff',
                cacheBust: true,
                width: widget.captureElement.offsetWidth,
                height: widget.captureElement.offsetHeight,
                skipAutoScale: true,
                includeQueryParams: true,
            });

            return dataUrl;
        } catch (error) {
            console.error('Error capturing widget:', error);
            return '';
        }
    }

    /**
     * Captures multiple widgets in batches to prevent browser freezing
     * Returns a map of widget elements to their captured image data URLs
     */
    static async captureWidgetsInBatches(
        widgets: WidgetPosition[],
        batchSize: number = 5,
        yieldInterval: number = 100,
        onProgress?: (captured: number, total: number) => void,
    ): Promise<Map<HTMLElement, string>> {
        const allImages: string[] = [];

        for (let i = 0; i < widgets.length; i += batchSize) {
            const batch = widgets.slice(i, i + batchSize);
            const batchImages = await Promise.all(
                batch.map((widget) => PptxExportUtils.captureWidgetAsImage(widget, widgets.length)),
            );
            allImages.push(...batchImages);

            // Yield to event loop between batches to keep UI responsive
            if (i + batchSize < widgets.length) {
                await new Promise((resolve) => setTimeout(resolve, yieldInterval));
            }

            const captured = Math.min(i + batchSize, widgets.length);
            onProgress?.(captured, widgets.length);
        }

        // Create a map for quick image lookup
        const imageMap = new Map<HTMLElement, string>();
        widgets.forEach((widget, index) => {
            imageMap.set(widget.element, allImages[index]);
        });

        return imageMap;
    }

    /**
     * Creates a cover page for the PPTX presentation matching PDF v2 style
     */
    static createCoverPage(
        pptx: Pptxgen,
        reportName: string,
        reportWidgets: Widget[],
        logoDataUrl: string | undefined,
    ): void {
        const slide = pptx.addSlide();

        // White background
        slide.background = { color: 'FFFFFF' };

        // Constants for layout
        const leftMargin = 0.5;
        const blackLineY = 0.4;
        const reportNameY = blackLineY;
        const clientLogoY = 0.35;
        const reportNameFontSize = 32;

        // Draw line above report name
        slide.addShape(pptx.ShapeType.line, {
            x: leftMargin,
            y: blackLineY,
            w: 2.5,
            h: 0,
            line: { color: CommonExportConstants.DARK_BLUE_COLOR, width: 1 },
        });

        // Report name below the line
        slide.addText(reportName, {
            x: leftMargin - 0.12,
            y: reportNameY,
            w: 8.0,
            h: 1,
            fontSize: reportNameFontSize,
            color: CommonExportConstants.DARK_BLUE_COLOR,
            align: 'left',
            valign: 'top',
        });

        // Client logo at top right
        if (logoDataUrl) {
            slide.addImage({
                data: logoDataUrl,
                x: 8.85,
                y: clientLogoY,
                sizing: { type: 'contain', w: 0.6, h: 0.6 },
            });
        }

        // Project-contract combinations
        const projectContracts = getUniqueProjectContracts(reportWidgets);

        if (projectContracts.length > 0) {
            const displayCount = Math.min(2, projectContracts.length);
            const itemSpacing = 0.7; // Gap between project items
            const moreTextSpacing = 0.45; // Smaller gap before "& n more" text

            // Position for project contracts - MUCH HIGHER
            const bottomAnchorY = 3.7;

            // Calculate total items including "& n more" if applicable
            const hasMoreText = projectContracts.length > 2;

            // Calculate starting Y by subtracting space needed for items above
            const totalSpacing = hasMoreText
                ? (displayCount - 1) * itemSpacing + moreTextSpacing
                : (displayCount - 1) * itemSpacing;
            let yPosition = bottomAnchorY - totalSpacing;

            for (let i = 0; i < displayCount; i++) {
                const { project, contract } = projectContracts[i];
                const text = `${project}: ${contract}`;

                slide.addText(text, {
                    x: leftMargin,
                    y: yPosition,
                    w: 8.5, // Max width to avoid overlapping with logo
                    h: 0.5,
                    fontSize: 40,
                    bold: true,
                    color: CommonExportConstants.DARK_BLUE_COLOR,
                    align: 'left',
                    fontFace: 'Helvetica',
                });

                // Use smaller spacing before "& n more" text if this is the last project item
                if (i === displayCount - 1 && projectContracts.length > 2) {
                    yPosition += itemSpacing;
                } else {
                    yPosition += itemSpacing;
                }
            }

            // Show "And n more..." if there are more than 2
            if (projectContracts.length > 2) {
                const remainingCount = projectContracts.length - 2;
                const moreText = `And ${remainingCount} more...`;

                slide.addText(moreText, {
                    x: leftMargin,
                    y: yPosition,
                    w: 8.5,
                    h: 0.35,
                    fontSize: 20,
                    bold: true,
                    color: CommonExportConstants.DARK_BLUE_COLOR,
                    align: 'left',
                });
            }
        }

        // "Powered By" text above logo
        slide.addText('Powered By', {
            x: leftMargin - 0.1,
            y: 4.84,
            w: 2.0,
            h: 0.15,
            fontSize: 8,
            color: CommonExportConstants.DARK_BLUE_COLOR,
            align: 'left',
        });

        // Foresight logo at bottom left
        slide.addImage({
            x: leftMargin,
            y: 5,
            path: foresightLogoFooter as any,
            w: 1.5,
            h: 0.23,
        });

        // Date at bottom right (aligned with Foresight logo)
        const today = new Date();
        const formattedDate = today.toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
        });

        slide.addText(formattedDate, {
            x: 7.2,
            y: 5,
            w: 2.3,
            h: 0.2,
            fontSize: 11,
            color: '2C3E50',
            align: 'right',
        });
    }
}
